import axios from 'axios';

// Get backend URL from environment or use default based on current location
const getBackendUrl = () => {
  // First check for environment variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // For production, derive from current hostname
  const hostname = window.location.hostname;
  
  // If we're on app.withnimble.com, use api.withnimble.com
  if (hostname === 'app.withnimble.com') {
    return 'https://api.withnimble.com';
  }
  
  // If we're on withnimble.com, use api.withnimble.com
  if (hostname === 'withnimble.com') {
    return 'https://api.withnimble.com';
  }
  
  // For any other production domain, try api subdomain
  if (!hostname.includes('localhost') && !hostname.includes('127.0.0.1') && !hostname.includes('preview.emergentagent.com')) {
    return `https://api.${hostname.replace('app.', '')}`;
  }
  
  // Default for development/preview
  return '';
};

const BACKEND_URL = getBackendUrl();
export { BACKEND_URL };
const API = `${BACKEND_URL}/api`;

console.log('API Base URL:', API); // Debug log

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  googleLogin: () => apiClient.post('/auth/google'),
  getMe: () => apiClient.get('/auth/me'),
  logout: () => apiClient.post('/auth/logout'),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => apiClient.post('/auth/reset-password', { token, new_password }),
  verifyResetToken: (token) => apiClient.get(`/auth/verify-reset-token/${token}`),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  resendVerification: () => apiClient.post('/auth/resend-verification'),
  updateUserRoles: (userId, roles) => apiClient.put(`/auth/users/${userId}/roles`, { roles }),
  getUserRoles: (userId) => apiClient.get(`/auth/users/${userId}/roles`),
};

// Task APIs
export const tasksAPI = {
  getAll: (params) => apiClient.get('/tasks', { params }),
  create: (data) => apiClient.post('/tasks', data),
  getById: (id) => apiClient.get(`/tasks/${id}`),
  update: (id, data) => apiClient.put(`/tasks/${id}`, data),
  delete: (id) => apiClient.delete(`/tasks/${id}`),
  updateStatus: (id, status) => apiClient.patch(`/tasks/${id}/status`, { status }),
  uploadAttachments: (id, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return apiClient.post(`/tasks/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadAttachment: (id, index) => apiClient.get(`/tasks/${id}/attachments/${index}`, { responseType: 'blob' }),
  addComment: (id, text) => apiClient.post(`/tasks/${id}/comments`, { text }),
  getComments: (id) => apiClient.get(`/tasks/${id}/comments`),
};

// Team APIs
export const teamAPI = {
  getAll: () => apiClient.get('/team'),
  create: (data) => apiClient.post('/team', data),
  getById: (id) => apiClient.get(`/team/${id}`),
  update: (id, data) => apiClient.put(`/team/${id}`, data),
  updateMember: (id, data) => apiClient.put(`/team/${id}`, data),
  delete: (id) => apiClient.delete(`/team/${id}`),
  getMyClients: () => apiClient.get('/team/my-clients'),
  getMyVAs: () => apiClient.get('/team/my-vas'),
};

// Ratings APIs
export const ratingsAPI = {
  getAll: (params) => apiClient.get('/ratings', { params }),
  getVARatings: (vaId) => apiClient.get(`/ratings/va/${vaId}`),
  getTrend: (vaId) => apiClient.get(`/ratings/trend/${vaId}`),
  getSummary: (vaId) => apiClient.get(`/ratings/summary/${vaId}`),
  create: (data) => apiClient.post('/ratings', data),
  adminCreate: (data) => apiClient.post('/ratings/admin', data),
  adminUpdate: (id, data) => apiClient.put(`/ratings/admin/${id}`, data),
  adminDelete: (id, vaId) => apiClient.delete(`/ratings/admin/${id}?va_id=${vaId}`),
};

// Profile APIs
export const profileAPI = {
  getMe: () => apiClient.get('/profile/me'),
  updateMe: (data) => apiClient.put('/profile/me', data),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/profile/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePhoto: () => apiClient.delete('/profile/photo'),
  adminUploadPhoto: (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/profile/admin/photo/${userId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  adminDeletePhoto: (userId) => apiClient.delete(`/profile/admin/photo/${userId}`),
};

// Email/System Status APIs
export const systemAPI = {
  getEmailStatus: () => apiClient.get('/email/status'),
  getEmailTemplates: () => apiClient.get('/admin/email-templates'),
  getEmailPreview: (templateType) => apiClient.get(`/email/templates/preview/${templateType}`),
  sendTestEmail: (templateType, email) => apiClient.post('/email/send-test-email', { template_type: templateType, email }),
};

// Analytics APIs
export const analyticsAPI = {
  getOverview: () => apiClient.get('/admin/analytics/overview'),
  getVAPerformance: () => apiClient.get('/admin/analytics/va-performance'),
  getClientEngagement: () => apiClient.get('/admin/analytics/client-engagement'),
};

// Invoices/Payments APIs
export const invoicesAPI = {
  getInvoices: () => apiClient.get('/payments/invoices'),
};

// Matching APIs
export const matchingAPI = {
  getRecommendedVAs: (clientId) => apiClient.get(`/admin/matching/recommended-vas/${clientId}`),
  assignVAToClient: (vaId, clientId) => apiClient.post('/admin/matching/assign-va', { va_id: vaId, client_id: clientId }),
};

// Chat APIs
export const chatAPI = {
  getConversations: () => apiClient.get('/chat/conversations'),
  createConversation: (participantId, subject) => apiClient.post('/chat/conversations', { participant_id: participantId, subject }),
  getMessages: (conversationId, limit = 50) => apiClient.get(`/chat/conversations/${conversationId}/messages?limit=${limit}`),
  sendMessage: (conversationId, content) => apiClient.post(`/chat/conversations/${conversationId}/messages`, { recipient_id: '', content, message_type: 'text' }),
  getUnreadCount: () => apiClient.get('/chat/unread-count'),
};

// Scheduling APIs
export const schedulingAPI = {
  getCompatibleVAs: (clientId) => apiClient.get(`/scheduling/compatible-vas/${clientId}`),
  getPreferences: () => apiClient.get('/scheduling/preferences'),
  setPreferences: (data) => apiClient.post('/scheduling/preferences', data),
  autoAssign: (clientId, taskTypes = [], urgency = 'normal') => apiClient.post(`/scheduling/auto-assign/${clientId}`, { task_types: taskTypes, urgency }),
  getTimezoneMatrix: () => apiClient.get('/scheduling/timezone-matrix'),
};

// Digest APIs
export const digestAPI = {
  preview: () => apiClient.get('/digest/preview'),
  sendNow: () => apiClient.post('/digest/send-now'),
  getHistory: (limit = 10) => apiClient.get(`/digest/history?limit=${limit}`),
  subscribe: () => apiClient.post('/digest/subscribe'),
  unsubscribe: () => apiClient.delete('/digest/subscribe'),
};

// Bandwidth APIs
export const bandwidthAPI = {
  getVABandwidth: (vaId) => apiClient.get(`/bandwidth/va/${vaId}`),
  getAllBandwidth: () => apiClient.get('/bandwidth/all'),
  allocateVA: (data) => apiClient.post('/bandwidth/allocate', data),
  updateUsage: (vaId, data) => apiClient.put(`/bandwidth/va/${vaId}/usage`, data),
  updateSettings: (vaId, totalHours) => apiClient.put(`/bandwidth/va/${vaId}/settings?total_hours=${totalHours}`),
  removeAllocation: (allocationId) => apiClient.delete(`/bandwidth/allocation/${allocationId}`),
  getMyBandwidth: () => apiClient.get('/bandwidth/my-allocation'),
  resetMonthly: () => apiClient.post('/bandwidth/reset-monthly'),
  setFullyBooked: (vaId, fullyBooked, notes) => apiClient.put(`/bandwidth/va/${vaId}/fully-booked`, { fully_booked: fullyBooked, notes }),
  notifyMe: (vaId) => apiClient.post(`/bandwidth/va/${vaId}/notify-me`),
  cancelNotifyMe: (vaId) => apiClient.delete(`/bandwidth/va/${vaId}/notify-me`),
  checkNotifyMe: (vaId) => apiClient.get(`/bandwidth/va/${vaId}/notify-me`),
};

// EOD Reports APIs
export const reportsAPI = {
  getAll: (params) => apiClient.get('/reports/eod', { params }),
  create: (data) => apiClient.post('/reports/eod', data),
  getById: (id) => apiClient.get(`/reports/eod/${id}`),
  addFeedback: (id, feedback) => apiClient.post(`/reports/eod/${id}/feedback`, { feedback }),
};

// Timesheet APIs
export const timesheetsAPI = {
  getAll: (params) => apiClient.get('/timesheets', { params }),
  create: (data) => apiClient.post('/timesheets', data),
  getById: (id) => apiClient.get(`/timesheets/${id}`),
  approve: (id) => apiClient.patch(`/timesheets/${id}/approve`),
  reject: (id) => apiClient.patch(`/timesheets/${id}/reject`),
};

// Leave APIs
export const leaveAPI = {
  getAll: () => apiClient.get('/leave'),
  create: (data) => apiClient.post('/leave', data),
  approve: (id) => apiClient.patch(`/leave/${id}/approve`),
  reject: (id) => apiClient.patch(`/leave/${id}/reject`),
};

// Performance APIs
export const performanceAPI = {
  getOverview: () => apiClient.get('/performance/overview'),
  getWeekly: () => apiClient.get('/performance/weekly'),
  getCategoryDistribution: () => apiClient.get('/performance/category-distribution'),
};

// VA Hiring APIs
export const vasAPI = {
  getAvailable: () => apiClient.get('/vas/available'),
  getById: (id) => apiClient.get(`/vas/${id}`),
  hire: (data) => apiClient.post('/vas/hire', data),
};

export default apiClient;
